import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance";
import OTPInput from "./OtpInput"; // Keep using your custom OTP input
import { ClipLoader } from "react-spinners"; // Spinner

const ModalComponent = ({
  show,
  onHide,
  email,
  onStatusChange,
  forgotPassword = false,
}) => {
  const [timer, setTimer] = useState(60);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let interval = null;
    if (show && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [show, timer]);

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = forgotPassword
        ? "/otp/forgot-password/verify-otp"
        : "/otp/verify-otp";

      const response = await axios.post(endpoint, { email, otp });

      if (response.status === 200) {
        if (forgotPassword) {
          setOtpVerified(true);
        } else {
          onStatusChange(true);
          onHide();
        }
        setError("");
      } else {
        setError("Invalid OTP or OTP expired");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
      setTimer(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async () => {
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "/otp/forgot-password/reset",
        { email, newPassword }
      );

      if (response.status === 200) {
        onStatusChange(true);
        onHide();
      } else {
        setError("Failed to update password");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;
    try {
      const endpoint = forgotPassword
        ? "/otp/forgot-password/send-otp"
        : "/otp/send-otp";

      await axios.post(endpoint, { email });
      setTimer(180);
      setError("");
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Failed to resend OTP. Try again.");
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-150 p-3">
      <div className="bg-gray-900 text-white p-6 rounded-lg w-full max-w-md shadow-lg relative">
        <button
          className="absolute top-3 right-4 text-gray-400 hover:text-white text-xl"
          onClick={onHide}
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold mb-4">Email Verification</h2>

        <p className="mb-4">
          {otpVerified && forgotPassword
            ? "Enter your new password"
            : `OTP has been sent to ${email}`}
        </p>

        {!otpVerified ? (
          <>
            <label className="block mb-2">Enter OTP:</label>
            <OTPInput setModalOTP={setOtp} />

            <div className="mt-3">
              {timer > 0 ? (
                <span className="text-sm text-gray-400">
                  Resend OTP in {timer} seconds
                </span>
              ) : (
                <button
                  className="mt-2 text-blue-500 hover:underline text-sm"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="block mb-2">New Password:</label>
            <div className="flex mb-3">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full p-2 rounded-l bg-gray-800 border border-gray-700 text-white"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="px-3 bg-gray-700 text-white rounded-r"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <label className="block mb-2">Confirm Password:</label>
            <div className="flex mb-3">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full p-2 rounded-l bg-gray-800 border border-gray-700 text-white"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                className="px-3 bg-gray-700 text-white rounded-r"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </>
        )}

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        <div className="mt-5 flex justify-end">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
            onClick={!otpVerified ? handleOtpSubmit : handleSetNewPassword}
            disabled={loading}
          >
            {loading ? <ClipLoader size={20} color="#fff" /> : otpVerified ? "Set Password" : "Verify OTP"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalComponent;
